"use client";

import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { muxStreamUrl, muxThumbnailUrl } from "@/lib/portfolio/mux-urls";

/**
 * HLS video player for a Mux playback id. Ported from react.pmbio's MuxPlayer:
 * uses native HLS on Safari (canPlayType) and hls.js everywhere else, and
 * retries the load with backoff while the asset is still processing (the
 * manifest 404s until Mux finishes transcoding).
 */
interface MuxPlayerProps {
  playbackId: string;
  className?: string;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  posterTime?: number;
  posterWidth?: number;
}

export default function MuxPlayer({
  playbackId,
  className,
  controls = true,
  autoPlay = false,
  muted = false,
  posterTime,
  posterWidth = 1280,
}: MuxPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  /*
   * Bumping this re-runs the effect and starts a fresh load. The retry budget
   * below used to be the end of the road: after ~60s of a still-processing
   * manifest the effect returned and the player was dead for good, with no
   * message and no recovery once Mux finished. The user was left with a poster
   * and a play button that did nothing — reported 2026-09-03.
   */
  const [attempt, setAttempt] = useState(0);
  const [gaveUp, setGaveUp] = useState(false);
  const src = muxStreamUrl(playbackId);
  const poster = muxThumbnailUrl(playbackId, {
    time: posterTime ?? 0,
    width: posterWidth,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    const MAX_RETRIES = 12; // ~60s with backoff capped at 6s

    /*
     * Watchdog on the only thing the viewer cares about: did the manifest ever
     * load. Deciding this from hls.js error events does not work — a manifest
     * 404 (the asset is still encoding) is retried internally for a long time
     * and may never surface as a FATAL error, so a give-up branch hung off
     * `data.fatal` never fires. Verified 2026-09-03 against a never-ready
     * playback id: 70s with no fatal error at all. A timer is both simpler and
     * matches the symptom, which is "it just spins".
     */
    const READY_DEADLINE_MS = 20_000;
    let loaded = false;
    const markLoaded = () => {
      loaded = true;
      if (!cancelled) setGaveUp(false);
    };
    video.addEventListener("loadedmetadata", markLoaded);
    const watchdog = setTimeout(() => {
      if (!cancelled && !loaded) setGaveUp(true);
    }, READY_DEADLINE_MS);
    const stopWatch = () => {
      clearTimeout(watchdog);
      video.removeEventListener("loadedmetadata", markLoaded);
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Native HLS (Safari). Retry by re-setting .src on errors until the Mux
      // asset finishes processing.
      let retries = 0;
      const loadWithRetry = () => {
        video.src = src;
      };
      const onError = () => {
        if (cancelled || retries >= MAX_RETRIES) return;
        const wait = Math.min(1000 * 2 ** retries, 6000);
        retries += 1;
        setTimeout(loadWithRetry, wait);
      };
      video.addEventListener("error", onError);
      loadWithRetry();
      return () => {
        cancelled = true;
        stopWatch();
        video.removeEventListener("error", onError);
      };
    }

    if (Hls.isSupported()) {
      // enableWorker: false deliberately. Our CSP has no worker-src, so it
      // falls back to `default-src 'self'` and Chrome blocks hls.js's
      // blob: worker — verified 2026-09-03: it logs a CSP violation and an
      // internalException, then silently degrades to the main thread anyway.
      // Asking for a worker we know is blocked only buys console noise.
      const hls = new Hls({ enableWorker: false });
      let retries = 0;
      const tryLoad = () => {
        hls.loadSource(src);
        hls.attachMedia(video);
      };
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (cancelled || !data.fatal) return;
        // Manifest 404 (asset still processing) or other recoverable errors →
        // restart the load with backoff.
        if (retries >= MAX_RETRIES) {
          // Out of budget. Say so instead of leaving a dead play button.
          setGaveUp(true);
          return;
        }
        const wait = Math.min(1000 * 2 ** retries, 6000);
        retries += 1;
        setTimeout(() => {
          if (cancelled) return;
          try {
            hls.recoverMediaError();
          } catch {
            // ignore — about to re-load anyway
          }
          tryLoad();
        }, wait);
      });
      hls.on(Hls.Events.MANIFEST_PARSED, markLoaded);
      tryLoad();
      return () => {
        cancelled = true;
        stopWatch();
        hls.destroy();
      };
    }

    // Last resort: let the browser try the manifest directly.
    video.src = src;
    return () => {
      cancelled = true;
      stopWatch();
    };
    // `attempt` is a deliberate dependency: bumping it re-runs the whole load.
  }, [src, attempt]);

  return (
    <div className="relative h-full w-full">
      <video
        ref={videoRef}
        poster={poster}
        controls={controls}
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        className={className ?? "h-full w-full"}
      />
      {gaveUp ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-ink/80 p-4 text-center">
          <p className="text-[13px] font-medium text-white">
            This video is still being processed.
          </p>
          <p className="text-xs text-white/70">
            Mux is finishing the encode. It is safely saved.
          </p>
          <button
            type="button"
            onClick={() => {
              setGaveUp(false);
              setAttempt((a) => a + 1);
            }}
            className="mt-1 inline-flex h-9 items-center rounded-md bg-white px-3.5 text-[13px] font-semibold text-ink hover:bg-neutral-100"
          >
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}
